/**
 * Custom pet portrait — product gallery with the customer's own scenes.
 *
 * Puts several slides at the FRONT of the product page's main gallery
 * (the customer's portrait in each room scene that matches their frame),
 * keeping the product's own photos after them so people can still scroll
 * through those. Builds the same markup as NavaBird's product-thumbnail
 * snippet (Dawn 6 style), with a guard for Dawn's slider divide-by-zero.
 *
 * window.NbWallGallery.sync([dataUrl, ...]) — adds, updates or removes
 * the custom slides to match the list; the first call shows the first one.
 */
(function () {
  const PREFIX = 'nb-scene';
  let count = 0;
  let shownOnce = false;

  function guardSliderUpdate(sliderEl) {
    if (!sliderEl || sliderEl.__nbGuarded || typeof sliderEl.update !== 'function') return;
    sliderEl.__nbGuarded = true;
    const originalUpdate = sliderEl.update;
    sliderEl.update = function guardedUpdate(...args) {
      if (!this.sliderItemOffset) return;
      return originalUpdate.apply(this, args);
    };
  }

  function refs() {
    const builder = document.querySelector('[data-instant-artwork-builder]');
    const sectionId = builder && builder.dataset.gallerySectionId;
    if (!sectionId) return null;
    const r = {
      sectionId,
      gallery: document.getElementById(`MediaGallery-${sectionId}`),
      viewerList: document.getElementById(`Slider-Gallery-${sectionId}`),
      viewerSlider: document.getElementById(`GalleryViewer-${sectionId}`),
      thumbsList: document.getElementById(`Slider-Thumbnails-${sectionId}`),
      thumbsSlider: document.getElementById(`GalleryThumbnails-${sectionId}`),
      modal: document.getElementById(`ProductModal-${sectionId}`),
    };
    if (!r.gallery || !r.viewerList) return null;
    guardSliderUpdate(r.viewerSlider);
    guardSliderUpdate(r.thumbsSlider);
    return r;
  }

  const ids = (r, i) => ({
    media: `${r.sectionId}-${PREFIX}-${i}`,
    slide: `Slide-${r.sectionId}-${PREFIX}-${i}`,
    thumb: `Thumbnail-${PREFIX}-${i}`,
    modal: `ModalMedia-${PREFIX}-${i}`,
  });

  function fadeIn(el) {
    el.style.opacity = '0';
    el.style.transition = 'opacity 0.25s ease';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      el.style.opacity = '1';
    }));
  }

  function addSlide(r, i, url, before) {
    const id = ids(r, i);
    const existingToggle = r.viewerList.querySelector('.product__media-toggle');
    const toggleClass = existingToggle ? existingToggle.className : 'product__media-toggle quick-add-hidden';
    const li = document.createElement('li');
    li.id = id.slide;
    li.className = 'product__media-item grid__item slider__slide';
    li.dataset.mediaId = id.media;
    li.innerHTML = `
      <modal-opener class="product__modal-opener product__modal-opener--image no-js-hidden"${r.modal ? ` data-modal="#${r.modal.id}"` : ''}>
        <div class="product__media media media--transparent gradient global-media-settings" style="padding-top: 100%;">
          <img id="${id.slide}-img" src="${url}" alt="Your pet's portrait in a room" loading="lazy">
        </div>
        <button class="${toggleClass}" type="button" aria-haspopup="dialog" data-media-id="${PREFIX}-${i}">
          <span class="visually-hidden">Open your portrait preview in a dialog</span>
        </button>
      </modal-opener>`;
    r.viewerList.insertBefore(li, before || r.viewerList.firstChild);
    fadeIn(li);

    if (r.thumbsList) {
      const t = document.createElement('li');
      t.className = 'thumbnail-list__item slider__slide';
      t.dataset.target = id.media;
      t.dataset.mediaPosition = String(i + 1);
      t.dataset.nbScene = String(i);
      t.innerHTML = `
        <button class="thumbnail global-media-settings global-media-settings--no-shadow thumbnail--narrow" type="button" aria-label="Your pet's portrait in a room" aria-controls="${r.viewerSlider ? r.viewerSlider.id : ''}">
          <img id="${id.thumb}" src="${url}" alt="Your pet's portrait in a room" width="200" height="200">
        </button>`;
      const tBefore = r.thumbsList.querySelector(`[data-nb-scene="${i + 1}"]`) || r.thumbsList.querySelector('.thumbnail-list__item:not([data-nb-scene])');
      r.thumbsList.insertBefore(t, tBefore);
      fadeIn(t);
      t.querySelector('button').addEventListener('click', () => {
        if (r.gallery.setActiveMedia) r.gallery.setActiveMedia(id.media, false);
      });
    }

    if (r.modal) {
      const content = r.modal.querySelector('.product-media-modal__content');
      if (content) {
        const img = document.createElement('img');
        img.id = id.modal;
        img.className = 'global-media-settings global-media-settings--no-shadow';
        img.dataset.mediaId = `${PREFIX}-${i}`;
        img.src = url;
        img.alt = "Your pet's portrait in a room";
        content.insertBefore(img, content.firstChild);
      }
    }
  }

  function removeSlide(r, i) {
    const id = ids(r, i);
    [document.getElementById(id.slide), document.getElementById(id.modal)].forEach((el) => el && el.remove());
    const thumb = document.getElementById(id.thumb);
    if (thumb) thumb.closest('li').remove();
  }

  // Swap an image without a blank frame: decode the new one first, then a
  // short cross-fade.
  function swapSrc(img, url) {
    if (!img || img.src === url) return;
    const next = new Image();
    next.src = url;
    const apply = () => {
      img.style.transition = 'opacity 0.18s ease';
      img.style.opacity = '0.35';
      requestAnimationFrame(() => {
        img.src = url;
        requestAnimationFrame(() => {
          img.style.opacity = '1';
        });
      });
    };
    (next.decode ? next.decode() : Promise.resolve()).then(apply, apply);
  }

  function sync(urls) {
    const r = refs();
    if (!r || !urls) return;
    const changedCount = urls.length !== count;
    // Remove slides we no longer need (from the end).
    for (let i = count - 1; i >= urls.length; i--) removeSlide(r, i);
    urls.forEach((url, i) => {
      const id = ids(r, i);
      const existing = document.getElementById(`${id.slide}-img`);
      if (existing) {
        swapSrc(existing, url);
        swapSrc(document.getElementById(id.thumb), url);
        const modalImg = document.getElementById(id.modal);
        if (modalImg) modalImg.src = url;
      } else {
        // In order: after the previous custom slide, before the product photos.
        const prev = i > 0 ? document.getElementById(ids(r, i - 1).slide) : null;
        addSlide(r, i, url, prev ? prev.nextSibling : r.viewerList.firstChild);
      }
    });
    count = urls.length;

    if (changedCount) {
      if (r.viewerSlider && typeof r.viewerSlider.resetPages === 'function') r.viewerSlider.resetPages();
      if (r.thumbsSlider && typeof r.thumbsSlider.resetPages === 'function') r.thumbsSlider.resetPages();
    }
    if ((!shownOnce || changedCount) && urls.length && r.gallery.setActiveMedia) {
      r.gallery.setActiveMedia(ids(r, 0).media, false);
      shownOnce = true;
    }
  }

  window.NbWallGallery = { sync };
})();
